import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Send, CalendarClock, Sparkles, Trash2, Megaphone, Users, Smartphone,
  Mail, Bell, ChevronLeft, ChevronRight, Plus, RefreshCw, Rss, BarChart3,
  Settings, Save, AppWindow, Link2, Copy,
} from "lucide-react";
import { Section, Empty, Pill, dt } from "./_shared";

const SEGMENTS = [
  ["all", "Alle"],
  ["active_today", "Heute aktiv"],
  ["inactive_1", "1 Tag inaktiv"],
  ["inactive_2", "2 Tage inaktiv"],
  ["inactive_3", "3 Tage inaktiv"],
  ["inactive_4", "4 Tage inaktiv"],
  ["inactive_5", "5 Tage inaktiv"],
  ["inactive_6", "6 Tage inaktiv"],
  ["inactive_7plus", "7+ Tage inaktiv"],
];
const DEEPLINKS = [
  ["breathwork", "Atemübung"],
  ["practices", "Übungen"],
  ["deepwithin", "Deep Within"],
  ["technique", "Atemtechnik vorschlagen"],
  ["none", "Nur App öffnen"],
  ["custom", "Eigener Link (URL)"],
];
const CHANNELS = [
  ["push", "Push", Smartphone],
  ["overlay", "Overlay", AppWindow],
  ["inapp", "In-App", Bell],
  ["email", "E-Mail", Mail],
  ["feed", "Feed", Rss],
];
const CHANNEL_LABEL = { push: "Push", overlay: "Overlay", inapp: "In-App", email: "E-Mail", feed: "Feed" };
const OVERLAY_LINKS = [
  ["none", "Kein Link"],
  ["breathwork", "Atemübung"],
  ["practices", "Übungen"],
  ["deepwithin", "Deep Within"],
  ["custom", "Eigener Link (URL)"],
];
const STATUS_TONE = { scheduled: "info", sending: "warn", sent: "good", failed: "bad", draft: "default" };
const STATUS_LABEL = { scheduled: "Geplant", sending: "Wird gesendet", sent: "Gesendet", failed: "Fehler", draft: "Entwurf" };

const segLabel = (s) => (SEGMENTS.find((x) => x[0] === s) || [s, s])[1];

export default function Campaigns() {
  const [tab, setTab] = useState("new");
  const [campaigns, setCampaigns] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState(null);

  const useAsTemplate = useCallback((msg) => { setTemplate(msg); setTab("new"); }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        api.get("/admin/campaigns"),
        api.get("/admin/campaign-messages?limit=500"),
      ]);
      setCampaigns(c.data?.campaigns || []);
      setMessages(m.data?.messages || []);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="space-y-4" data-testid="admin-campaigns-page">
      <PageHeader eyebrow="Admin" title="Kampagnen & Push"
        subtitle="Push, E-Mail und In-App-Nachrichten erstellen, per KI texten und als Funnel über Wochen planen."
        action={<Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={loadAll} data-testid="admin-campaigns-reload"><RefreshCw className="h-4 w-4" /> Aktualisieren</Button>} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-2 flex h-auto w-full flex-wrap justify-start gap-1 bg-secondary/60 p-1">
          <TabsTrigger value="new" data-testid="admin-campaigns-tab-new">Neu</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="admin-campaigns-tab-calendar">Kalender</TabsTrigger>
          <TabsTrigger value="history" data-testid="admin-campaigns-tab-history">Verlauf</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="admin-campaigns-tab-campaigns">Kampagnen</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-campaigns-tab-settings">Einstellungen</TabsTrigger>
        </TabsList>

        <TabsContent value="new">
          <Compose campaigns={campaigns} onSent={loadAll} template={template} onTemplateConsumed={() => setTemplate(null)} />
        </TabsContent>
        <TabsContent value="calendar">
          <CalendarView messages={messages} loading={loading} />
        </TabsContent>
        <TabsContent value="history">
          <History messages={messages} loading={loading} onChanged={loadAll} onUseTemplate={useAsTemplate} />
        </TabsContent>
        <TabsContent value="campaigns">
          <CampaignsTab campaigns={campaigns} onChanged={loadAll} />
        </TabsContent>
        <TabsContent value="settings">
          <SettingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ───────────────────────── Compose + AI ───────────────────────── */

function toLocalDatetimeInput(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function Compose({ campaigns, onSent, template, onTemplateConsumed }) {
  const [campaignId, setCampaignId] = useState("");
  const [baseTitle, setBaseTitle] = useState("");
  const [baseBody, setBaseBody] = useState("");
  const [channels, setChannels] = useState(["push"]);
  const [cc, setCc] = useState({}); // per-channel { title, body, cta_label }
  const [deeplink, setDeeplink] = useState("breathwork");
  const [linkUrl, setLinkUrl] = useState("");
  const [overlayLink, setOverlayLink] = useState("none");
  const [overlayLinkUrl, setOverlayLinkUrl] = useState("");
  const [overlayFreq, setOverlayFreq] = useState("once");
  const [overlayExpires, setOverlayExpires] = useState("");
  const [segment, setSegment] = useState("all");
  const [includeCoaches, setIncludeCoaches] = useState(false);
  const [emailFallback, setEmailFallback] = useState(false);
  const [techniqueId, setTechniqueId] = useState("");
  const [techniques, setTechniques] = useState([]);
  const [abEnabled, setAbEnabled] = useState(false);
  const [titleB, setTitleB] = useState("");
  const [bodyB, setBodyB] = useState("");
  const [abAutoPromote, setAbAutoPromote] = useState(false);
  const [abPromoteHours, setAbPromoteHours] = useState(24);
  const [mode, setMode] = useState("now"); // now | schedule
  const [when, setWhen] = useState("");
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  // AI panel
  const [aiTopic, setAiTopic] = useState("");
  const [aiCount, setAiCount] = useState(3);
  const [aiTone, setAiTone] = useState("");
  const [aiSeries, setAiSeries] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMultiBusy, setAiMultiBusy] = useState(false);
  const [variants, setVariants] = useState([]);

  const fetchPreview = useCallback(async () => {
    setPreview(null);
    try {
      const { data } = await api.get(`/admin/push/audience-preview?segment=${segment}&include_coaches=${includeCoaches}`);
      setPreview(data);
    } catch { setPreview({ targeted_users: "?", with_push_tokens: "?" }); }
  }, [segment, includeCoaches]);
  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  useEffect(() => {
    if (deeplink === "technique" && techniques.length === 0) {
      api.get("/breathing-techniques").then(({ data }) => setTechniques(data || [])).catch(() => setTechniques([]));
    }
  }, [deeplink, techniques.length]);

  // Prefill the composer from a previously sent/scheduled message ("Als
  // Vorlage verwenden" in the Verlauf tab). Schedule is NOT copied — the
  // admin reviews + sends fresh.
  useEffect(() => {
    if (!template) return;
    const m = template;
    setCampaignId(m.campaign_id || "");
    setChannels(m.channels && m.channels.length ? m.channels : ["push"]);
    setBaseTitle(m.title || "");
    setBaseBody(m.body || "");
    setCc(m.channel_content || {});
    let dl = m.deeplink_key || "breathwork";
    let tId = "";
    let lUrl = "";
    if (dl === "custom") {
      const link = m.deeplink || "";
      if (link.includes("breakout-session") && /pickedTechniqueId=/.test(link)) {
        dl = "technique";
        const mt = link.match(/pickedTechniqueId=([^&]+)/);
        tId = mt ? decodeURIComponent(mt[1]) : "";
      } else {
        lUrl = link;
      }
    }
    setDeeplink(dl);
    setTechniqueId(tId);
    setLinkUrl(lUrl);
    if (m.overlay) {
      setOverlayLink(m.overlay.link_key || "none");
      setOverlayLinkUrl(m.overlay.link_key === "custom" ? (m.overlay.link || "") : "");
      setOverlayFreq(m.overlay.frequency || "once");
      setOverlayExpires(m.overlay.expires_at ? toLocalDatetimeInput(m.overlay.expires_at) : "");
    } else {
      setOverlayLink("none"); setOverlayLinkUrl(""); setOverlayFreq("once"); setOverlayExpires("");
    }
    setSegment(m.segment || "all");
    setIncludeCoaches(!!m.include_coaches);
    setEmailFallback(!!m.email_fallback);
    setAbEnabled(!!m.ab_enabled);
    setTitleB(m.variant_b_title || "");
    setBodyB(m.variant_b_body || "");
    setAbAutoPromote(!!m.ab_auto_promote);
    setAbPromoteHours(m.ab_promote_after_hours || 24);
    setMode("now"); setWhen("");
    toast.success("Vorlage geladen – anpassen und senden.");
    onTemplateConsumed?.();
  }, [template, onTemplateConsumed]);

  const toggleChannel = (c) => setChannels((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));
  const updateCc = (ch, field, val) => setCc((p) => ({ ...p, [ch]: { ...(p[ch] || {}), [field]: val } }));

  const generate = async () => {
    if (aiTopic.trim().length < 3) { toast.error("Bitte ein Thema eingeben."); return; }
    setAiBusy(true); setVariants([]);
    try {
      const { data } = await api.post("/admin/push/ai-generate", {
        topic: aiTopic.trim(),
        channel: channels.includes("email") && !channels.includes("push") ? "email" : "push",
        count: aiCount,
        tone: aiTone.trim() || undefined,
        series: aiSeries,
        language: "de",
      });
      setVariants(data?.variants || []);
      if (!data?.variants?.length) toast.message("Keine Texte erhalten — bitte erneut versuchen.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setAiBusy(false); }
  };

  const generateMulti = async () => {
    if (aiTopic.trim().length < 3) { toast.error("Bitte ein Thema eingeben."); return; }
    if (channels.length === 0) { toast.error("Mindestens einen Kanal wählen."); return; }
    setAiMultiBusy(true);
    try {
      const { data } = await api.post("/admin/push/ai-generate-multi", {
        topic: aiTopic.trim(),
        channels,
        tone: aiTone.trim() || undefined,
        language: "de",
      });
      const got = data?.channels || {};
      setCc((p) => ({ ...p, ...got }));
      const seed = got.push || got.overlay || got.inapp || got.email || got.feed || Object.values(got)[0];
      if (seed) { setBaseTitle((t) => t || seed.title || ""); setBaseBody((b) => b || seed.body || ""); }
      toast.success("Stimmige Texte für alle Kanäle erzeugt.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setAiMultiBusy(false); }
  };

  const applyVariant = (v, slot = "a") => {
    if (slot === "b") { setTitleB(v.title || ""); setBodyB(v.body || ""); }
    else { setBaseTitle(v.title || ""); setBaseBody(v.body || ""); }
    toast.success("Text übernommen.");
  };

  const resetForm = () => {
    setBaseTitle(""); setBaseBody(""); setCc({}); setWhen(""); setVariants([]);
    setTitleB(""); setBodyB(""); setAbAutoPromote(false); setAbPromoteHours(24);
    setLinkUrl(""); setOverlayLink("none"); setOverlayLinkUrl(""); setOverlayFreq("once"); setOverlayExpires("");
    setEmailFallback(false); setTechniqueId("");
  };

  const submit = async () => {
    if (!baseTitle.trim() || !baseBody.trim()) { toast.error("Basis-Titel und -Text sind erforderlich."); return; }
    if (channels.length === 0) { toast.error("Mindestens einen Kanal wählen."); return; }
    if (abEnabled && (!titleB.trim() || !bodyB.trim())) { toast.error("Für A/B bitte Titel B und Text B ausfüllen."); return; }
    if (deeplink === "custom") {
      const l = linkUrl.trim();
      if (!l) { toast.error("Bitte einen Push-Link angeben."); return; }
      if (!/^(https?:\/\/|\/|mywithin:\/\/)/.test(l)) { toast.error("Link muss mit http(s)://, / oder mywithin:// beginnen."); return; }
    }
    if (deeplink === "technique" && !techniqueId) { toast.error("Bitte eine Atemtechnik wählen."); return; }
    if (channels.includes("overlay")) {
      if (overlayLink === "custom") {
        const l = overlayLinkUrl.trim();
        if (!l) { toast.error("Bitte einen Overlay-Link angeben."); return; }
        if (!/^(https?:\/\/|\/|mywithin:\/\/)/.test(l)) { toast.error("Overlay-Link muss mit http(s)://, / oder mywithin:// beginnen."); return; }
      }
      if (overlayFreq === "always" && !overlayExpires) { toast.error("Für „bei jedem Start“ bitte ein Ablaufdatum setzen."); return; }
      if (overlayExpires && new Date(overlayExpires).getTime() <= Date.now()) { toast.error("Overlay-Ablaufdatum muss in der Zukunft liegen."); return; }
    }
    let scheduled_at = null;
    if (mode === "schedule") {
      if (!when) { toast.error("Bitte Datum/Uhrzeit wählen."); return; }
      const d = new Date(when);
      if (isNaN(d.getTime())) { toast.error("Ungültiges Datum."); return; }
      if (d.getTime() <= Date.now()) { toast.error("Bitte einen Zeitpunkt in der Zukunft wählen."); return; }
      scheduled_at = d.toISOString();
    }

    const cleanedCc = {};
    for (const ch of channels) {
      const c = cc[ch] || {};
      const entry = {};
      if ((c.title || "").trim()) entry.title = c.title.trim();
      if ((c.body || "").trim()) entry.body = c.body.trim();
      if (ch === "overlay" && (c.cta_label || "").trim()) entry.cta_label = c.cta_label.trim();
      if (Object.keys(entry).length) cleanedCc[ch] = entry;
    }

    setSending(true);
    try {
      await api.post("/admin/campaign-messages", {
        campaign_id: campaignId || null,
        channels,
        title: baseTitle.trim(),
        body: baseBody.trim(),
        channel_content: cleanedCc,
        deeplink_key: deeplink === "technique" ? "custom" : deeplink,
        link_url: deeplink === "custom"
          ? linkUrl.trim()
          : deeplink === "technique"
          ? `/(client)/breakout-session?pickedTechniqueId=${encodeURIComponent(techniqueId)}&src=push`
          : null,
        overlay_link_key: channels.includes("overlay") ? overlayLink : "none",
        overlay_link_url: channels.includes("overlay") && overlayLink === "custom" ? overlayLinkUrl.trim() : null,
        overlay_frequency: overlayFreq,
        overlay_expires_at: channels.includes("overlay") && overlayExpires ? new Date(overlayExpires).toISOString() : null,
        segment,
        include_coaches: includeCoaches,
        email_fallback: channels.includes("push") && !channels.includes("email") ? emailFallback : false,
        ab_enabled: abEnabled,
        variant_b_title: abEnabled ? titleB.trim() : null,
        variant_b_body: abEnabled ? bodyB.trim() : null,
        ab_auto_promote: abEnabled ? abAutoPromote : false,
        ab_promote_after_hours: abEnabled && abAutoPromote ? Math.max(1, Math.min(168, Number(abPromoteHours) || 24)) : 24,
        scheduled_at,
      });
      toast.success(mode === "schedule" ? "Nachricht geplant." : "Nachricht wird gesendet.");
      resetForm();
      onSent?.();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSending(false); }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Compose */}
      <Section title="Nachricht erstellen" testId="admin-campaigns-compose">
        <div className="space-y-3">
          <Field label="Kampagne (optional)">
            <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className={selCls} data-testid="admin-campaign-select">
              <option value="">— Einzelnachricht —</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>

          <Field label="Kanäle (Mehrfachauswahl)">
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map(([c, lbl, Icon]) => (
                <button key={c} type="button" onClick={() => toggleChannel(c)} data-testid={`admin-campaign-channel-${c}`}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm ${channels.includes(c) ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`}>
                  <Icon className="h-4 w-4" /> {lbl}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Basistext (Standard & Fallback für alle Kanäle)">
            <Input value={baseTitle} onChange={(e) => setBaseTitle(e.target.value)} maxLength={120} placeholder="Titel, z. B. 2 Minuten Atem für den Abend" className="mb-1.5" data-testid="admin-campaign-title" />
            <Textarea value={baseBody} onChange={(e) => setBaseBody(e.target.value)} maxLength={2000} className="min-h-[80px]" placeholder="Kurze, motivierende Nachricht …" data-testid="admin-campaign-body" />
          </Field>

          {channels.length > 0 && (
            <div className="rounded-xl border border-border bg-secondary/30 p-3" data-testid="admin-campaign-perchannel">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Eigener Text je Kanal (leer = Basistext)</p>
              <div className="space-y-2">
                {channels.map((ch) => (
                  <ChannelText key={ch} ch={ch} label={CHANNEL_LABEL[ch] || ch} content={cc[ch] || {}} onChange={updateCc} />
                ))}
              </div>
            </div>
          )}

          {channels.includes("overlay") && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-3" data-testid="admin-campaign-overlay-config">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
                <AppWindow className="h-3.5 w-3.5" /> Overlay-Einstellungen
              </p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Button-Ziel / Link">
                    <select value={overlayLink} onChange={(e) => setOverlayLink(e.target.value)} className={selCls} data-testid="admin-overlay-link">
                      {OVERLAY_LINKS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </Field>
                  <Field label="Häufigkeit">
                    <select value={overlayFreq} onChange={(e) => setOverlayFreq(e.target.value)} className={selCls} data-testid="admin-overlay-frequency">
                      <option value="once">Einmalig (bis geschlossen)</option>
                      <option value="always">Bei jedem Start (bis Ablauf)</option>
                    </select>
                  </Field>
                </div>
                {overlayLink === "custom" && (
                  <Field label="Overlay-Link-URL">
                    <Input value={overlayLinkUrl} onChange={(e) => setOverlayLinkUrl(e.target.value)} placeholder="https://… oder /app/breathwork" data-testid="admin-overlay-link-url" />
                  </Field>
                )}
                <Field label={`Ablaufdatum${overlayFreq === "always" ? " (erforderlich)" : " (optional)"}`}>
                  <Input type="datetime-local" value={overlayExpires} onChange={(e) => setOverlayExpires(e.target.value)} data-testid="admin-overlay-expires" />
                </Field>
                <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                  <Link2 className="mt-0.5 h-3 w-3 shrink-0" />
                  Das Overlay erscheint als Popup in der Web-App. Button öffnet App-Pfade (/…) direkt bzw. externe Links (https://) im Browser.
                </p>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-border bg-secondary/30 p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground">
              <input type="checkbox" checked={abEnabled} onChange={(e) => setAbEnabled(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="admin-campaign-ab" />
              A/B-Test (zwei Varianten, Segment wird 50/50 geteilt)
            </label>
            {abEnabled && (
              <div className="mt-3 space-y-3">
                <p className="text-[11px] text-muted-foreground">A/B testet den Basistext (A) gegen Variante B über Push &amp; In-App. Pro-Kanal-Texte gelten für Variante A.</p>
                <Field label="Titel B"><Input value={titleB} onChange={(e) => setTitleB(e.target.value)} maxLength={120} data-testid="admin-campaign-title-b" /></Field>
                <Field label="Text B"><Textarea value={bodyB} onChange={(e) => setBodyB(e.target.value)} maxLength={2000} className="min-h-[70px]" data-testid="admin-campaign-body-b" /></Field>
                <div className="rounded-lg border border-border bg-background p-2.5">
                  <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <input type="checkbox" checked={abAutoPromote} onChange={(e) => setAbAutoPromote(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="admin-campaign-ab-autopromote" />
                    Gewinner automatisch nachspielen (nur Push)
                  </label>
                  {abAutoPromote && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Nach</span>
                      <Input type="number" min={1} max={168} value={abPromoteHours} onChange={(e) => setAbPromoteHours(e.target.value)} className="h-9 w-20" data-testid="admin-campaign-ab-promote-hours" />
                      <span className="text-xs text-muted-foreground">Std. wird der stärkere Text an die schwächer bespielte Hälfte gesendet. Admins werden danach benachrichtigt.</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Push-Ziel / Link">
              <select value={deeplink} onChange={(e) => setDeeplink(e.target.value)} className={selCls} data-testid="admin-campaign-deeplink">
                {DEEPLINKS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Zielgruppe">
              <select value={segment} onChange={(e) => setSegment(e.target.value)} className={selCls} data-testid="admin-campaign-segment">
                {SEGMENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          </div>

          {deeplink === "custom" && (
            <Field label="Push-Link-URL (in die App oder extern)">
              <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://… oder /(client)/breathwork" data-testid="admin-campaign-link-url" />
              <p className="mt-1 text-xs text-muted-foreground">Externe Links (https://) öffnen im Browser, App-Pfade (/…) direkt in der App.</p>
            </Field>
          )}

          {deeplink === "technique" && (
            <Field label="Atemtechnik (aus der Bibliothek)">
              <select value={techniqueId} onChange={(e) => setTechniqueId(e.target.value)} className={selCls} data-testid="admin-campaign-technique">
                <option value="">— Technik wählen —</option>
                {techniques.map((tq) => <option key={tq.id} value={tq.id}>{tq.name}</option>)}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">Die Push öffnet direkt die Sitzung (Gefühl → Situation → Intensität) mit dieser Technik. Bei Kontraindikationen wählt die App automatisch eine sichere Alternative.</p>
            </Field>
          )}

          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={includeCoaches} onChange={(e) => setIncludeCoaches(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="admin-campaign-coaches" />
            Coaches einschließen
          </label>

          {channels.includes("push") && !channels.includes("email") && (
            <label className="flex items-start gap-2 rounded-xl border border-border bg-secondary/30 p-3 text-sm text-foreground" data-testid="admin-campaign-email-fallback-row">
              <input type="checkbox" checked={emailFallback} onChange={(e) => setEmailFallback(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" data-testid="admin-campaign-email-fallback" />
              <span>
                Nicht per Push erreichbare Nutzer zusätzlich per E-Mail anschreiben
                <span className="mt-0.5 block text-[11px] text-muted-foreground">
                  Nutzer mit aktivem Push-Gerät bekommen die Push; alle ohne Token (mit hinterlegter E-Mail) erhalten denselben Text automatisch per E-Mail.
                </span>
              </span>
            </label>
          )}

          <div className="rounded-xl border border-border bg-secondary/40 p-3 text-sm" data-testid="admin-campaign-preview">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 shrink-0 text-primary" />
              {preview === null ? <span className="text-muted-foreground">Empfänger werden berechnet …</span> : (
                <span className="text-foreground">
                  <b>{preview.targeted_users}</b> Empfänger
                  {channels.includes("push") && <> · <b>{preview.with_push_tokens}</b> Nutzer mit Push-Gerät · <b>{preview.device_tokens ?? "—"}</b> Geräte</>}
                </span>
              )}
            </div>
            {channels.includes("push") && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Momentaufnahme jetzt. Die im Verlauf zugestellte Zahl kann abweichen — bis zum Versand kommen/gehen Geräte, und abgelaufene oder abgemeldete Tokens scheitern beim Senden.
              </p>
            )}
          </div>

          <Field label="Versand">
            <div className="flex gap-2">
              <button type="button" onClick={() => setMode("now")} data-testid="admin-campaign-mode-now" className={`flex-1 rounded-full border px-3 py-2 text-sm ${mode === "now" ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`}>Jetzt senden</button>
              <button type="button" onClick={() => setMode("schedule")} data-testid="admin-campaign-mode-schedule" className={`flex-1 rounded-full border px-3 py-2 text-sm ${mode === "schedule" ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`}>Planen</button>
            </div>
          </Field>
          {mode === "schedule" && (
            <div>
              <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="admin-campaign-when" />
              <p className="mt-1 text-xs text-muted-foreground">
                Minutengenauer Versand in deiner lokalen Zeitzone.{when ? ` Geplant für: ${new Date(when).toLocaleString("de-DE")}` : ""}
              </p>
            </div>
          )}

          <Button className="w-full gap-1.5 rounded-full" onClick={submit} disabled={sending} data-testid="admin-campaign-submit">
            {mode === "schedule" ? <CalendarClock className="h-4 w-4" /> : <Send className="h-4 w-4" />}
            {sending ? "…" : mode === "schedule" ? "Planen" : "Jetzt senden"}
          </Button>
        </div>
      </Section>

      {/* AI assistant */}
      <Section title="KI-Textassistent" testId="admin-campaigns-ai">
        <div className="space-y-3">
          <Field label="Worum soll es gehen?">
            <Textarea value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} maxLength={2000} className="min-h-[70px]" placeholder="z. B. Nutzer zurückholen, die seit 3 Tagen inaktiv sind — sanfte Atemübung am Abend" data-testid="admin-ai-topic" />
          </Field>
          <Field label="Tonalität (optional)">
            <Input value={aiTone} onChange={(e) => setAiTone(e.target.value)} maxLength={200} placeholder="z. B. warm, motivierend" data-testid="admin-ai-tone" />
          </Field>

          <Button className="w-full gap-1.5 rounded-full" onClick={generateMulti} disabled={aiMultiBusy} data-testid="admin-ai-generate-multi">
            <Sparkles className="h-4 w-4" /> {aiMultiBusy ? "Stimmt ab …" : "Stimmige Texte für alle Kanäle"}
          </Button>
          <p className="text-[11px] text-muted-foreground">
            Erzeugt für jeden gewählten Kanal ({channels.map((c) => CHANNEL_LABEL[c] || c).join(", ") || "—"}) einen kanalgerechten Text mit rotem Faden — ohne Dopplungen.
          </p>

          <div className="rounded-xl border border-border bg-secondary/30 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Einzel-Varianten (für Basistext / A-B)</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Varianten">
                <select value={aiCount} onChange={(e) => setAiCount(Number(e.target.value))} className={selCls} data-testid="admin-ai-count">
                  {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </Field>
              <div className="flex items-end">
                <label className="flex items-center gap-2 pb-2 text-sm text-foreground">
                  <input type="checkbox" checked={aiSeries} onChange={(e) => setAiSeries(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="admin-ai-series" />
                  Serie
                </label>
              </div>
            </div>
            <Button variant="outline" className="mt-2 w-full gap-1.5 rounded-full" onClick={generate} disabled={aiBusy} data-testid="admin-ai-generate">
              <Sparkles className="h-4 w-4" /> {aiBusy ? "Generiere …" : "Varianten generieren"}
            </Button>

            {variants.length > 0 && (
              <div className="mt-2 space-y-2" data-testid="admin-ai-variants">
                {variants.map((v, i) => (
                  <div key={i} className="rounded-xl border border-border bg-card p-3" data-testid={`admin-ai-variant-${i}`}>
                    <p className="text-sm font-semibold text-foreground">{v.title}</p>
                    <p className="mt-0.5 text-sm text-muted-foreground">{v.body}</p>
                    <div className="mt-2 flex gap-1.5">
                      <Button size="sm" variant="ghost" className="h-7 gap-1 rounded-full text-xs text-primary" onClick={() => applyVariant(v, "a")} data-testid={`admin-ai-apply-${i}`}>
                        <Plus className="h-3 w-3" /> {abEnabled ? "Als A (Basis)" : "Als Basistext"}
                      </Button>
                      {abEnabled && (
                        <Button size="sm" variant="ghost" className="h-7 gap-1 rounded-full text-xs text-primary" onClick={() => applyVariant(v, "b")} data-testid={`admin-ai-applyb-${i}`}>
                          <Plus className="h-3 w-3" /> Als B
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}

function ChannelText({ ch, label, content, onChange }) {
  return (
    <div className="rounded-lg border border-border bg-background p-2.5" data-testid={`admin-cc-block-${ch}`}>
      <p className="mb-1.5 text-xs font-semibold text-foreground">{label}</p>
      <Input value={content.title || ""} onChange={(e) => onChange(ch, "title", e.target.value)} maxLength={160} placeholder="Titel (leer = Basistext)" className="mb-1.5" data-testid={`admin-cc-title-${ch}`} />
      <Textarea value={content.body || ""} onChange={(e) => onChange(ch, "body", e.target.value)} maxLength={2000} placeholder="Text (leer = Basistext)" className="min-h-[56px]" data-testid={`admin-cc-body-${ch}`} />
      {ch === "overlay" && (
        <Input value={content.cta_label || ""} onChange={(e) => onChange(ch, "cta_label", e.target.value)} maxLength={24} placeholder="Button-Label (optional, z. B. Jetzt starten)" className="mt-1.5" data-testid="admin-cc-cta-overlay" />
      )}
    </div>
  );
}

const selCls = "h-10 w-full rounded-lg border border-border bg-background px-2 text-sm text-foreground";
function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

/* ───────────────────────── Calendar ───────────────────────── */

function CalendarView({ messages, loading }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const byDay = useMemo(() => {
    const map = {};
    for (const m of messages) {
      const raw = m.scheduled_at || m.sent_at || m.created_at;
      if (!raw) continue;
      const d = new Date(raw);
      if (isNaN(d.getTime())) continue;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      (map[key] = map[key] || []).push(m);
    }
    return map;
  }, [messages]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const monthLabel = cursor.toLocaleDateString("de-DE", { month: "long", year: "numeric" });

  return (
    <Section testId="admin-campaigns-calendar"
      title={<span className="capitalize">{monthLabel}</span>}
      action={
        <div className="flex gap-1">
          <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="admin-cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="admin-cal-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      }>
      {loading ? <Empty text="Lädt …" /> : (
        <>
          <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase text-muted-foreground">
            {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (d === null) return <div key={`e${i}`} className="min-h-[72px] rounded-lg" />;
              const key = `${year}-${month}-${d}`;
              const items = byDay[key] || [];
              const today = new Date(); const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;
              return (
                <div key={key} data-testid={`admin-cal-day-${d}`} className={`min-h-[72px] rounded-lg border p-1 ${isToday ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
                  <div className="text-[11px] font-medium text-muted-foreground">{d}</div>
                  <div className="mt-0.5 space-y-0.5">
                    {items.slice(0, 3).map((m) => (
                      <div key={m.id} title={`${m.title} · ${STATUS_LABEL[m.status] || m.status}`} className={`truncate rounded px-1 py-0.5 text-[10px] font-medium ${pillBg(m.status)}`}>
                        {m.title}
                      </div>
                    ))}
                    {items.length > 3 && <div className="text-[10px] text-muted-foreground">+{items.length - 3} mehr</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Section>
  );
}
function pillBg(status) {
  return {
    scheduled: "bg-sky-100 text-sky-700",
    sending: "bg-amber-100 text-amber-700",
    sent: "bg-emerald-100 text-emerald-700",
    failed: "bg-rose-100 text-rose-700",
    draft: "bg-secondary text-muted-foreground",
  }[status] || "bg-secondary text-muted-foreground";
}

/* ───────────────────────── History ───────────────────────── */

function History({ messages, loading, onChanged, onUseTemplate }) {
  const [busy, setBusy] = useState(null);
  const [reports, setReports] = useState({});
  const [repBusy, setRepBusy] = useState(null);

  // Auto-load the impact report for sent messages so the full "Wirkung"
  // shows on every push without a click (cap keeps it light).
  useEffect(() => {
    const sent = messages.filter((m) => m.status === "sent").slice(0, 40);
    let cancelled = false;
    (async () => {
      for (const m of sent) {
        if (cancelled) break;
        try {
          const { data } = await api.get(`/admin/campaign-messages/${m.id}/report`);
          if (!cancelled) setReports((p) => ({ ...p, [m.id]: data }));
        } catch { /* best-effort */ }
      }
    })();
    return () => { cancelled = true; };
  }, [messages]);

  const loadReport = async (m) => {
    setRepBusy(m.id);
    try { const { data } = await api.get(`/admin/campaign-messages/${m.id}/report`); setReports((p) => ({ ...p, [m.id]: data })); }
    catch (err) { toast.error(apiError(err)); }
    finally { setRepBusy(null); }
  };

  const del = async (m) => {
    setBusy(m.id);
    try { await api.delete(`/admin/campaign-messages/${m.id}`); toast.success("Gelöscht."); onChanged?.(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };
  const sendNow = async (m) => {
    setBusy(m.id);
    try { await api.post(`/admin/campaign-messages/${m.id}/send-now`); toast.success("Wird gesendet."); onChanged?.(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  if (loading) return <Empty text="Lädt …" />;
  if (!messages.length) return <Empty text="Noch keine Nachrichten." />;

  return (
    <div className="space-y-2" data-testid="admin-campaigns-history">
      <div className="rounded-xl border border-border bg-secondary/30 p-3 text-[11px] leading-relaxed text-muted-foreground" data-testid="admin-history-note">
        <b className="text-foreground">Zahlen erklärt:</b> „Empfänger“ = Nutzer im Segment · „Push · Nutzer erreicht“ = Nutzer,
        denen zum Sendezeitpunkt mindestens ein Push zugestellt wurde · „Push · Geräte“ = Summe aller bespielten Geräte
        (ein Nutzer kann mehrere haben) · „Wirkung“ misst In-App-Öffnungen und Rückkehrer nach dem Versand.
        <br />
        Die Nutzer-/Geräte-Zahl im <b className="text-foreground">Versandfenster</b> ist eine Momentaufnahme (aktuell verfügbare
        Geräte). Im <b className="text-foreground">Verlauf</b> zählt die tatsächliche Zustellung zum Sendezeitpunkt — darum weichen
        die Zahlen ab (abgelaufene/abgemeldete Tokens scheitern, Geräte kommen bis zum Versand hinzu oder fallen weg).
      </div>
      {messages.map((m) => (
        <div key={m.id} data-testid={`admin-message-${m.id}`} className="rounded-xl border border-border bg-card p-3">
          <div className="flex items-start gap-2">
            <Megaphone className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate text-sm font-semibold text-foreground">{m.title}</p>
                <Pill tone={STATUS_TONE[m.status] || "default"}>{STATUS_LABEL[m.status] || m.status}</Pill>
                {(m.channels || []).map((c) => <Pill key={c} tone="info">{CHANNEL_LABEL[c] || c}</Pill>)}
              </div>
              <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{m.body}</p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {segLabel(m.segment)}{m.include_coaches ? " · inkl. Coaches" : ""}
                {m.scheduled_at ? ` · geplant: ${dt(m.scheduled_at)}` : m.sent_at ? ` · gesendet: ${dt(m.sent_at)}` : ` · erstellt: ${dt(m.created_at)}`}
              </p>
              {m.status === "sent" && m.stats && (
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`admin-message-stats-${m.id}`}>
                  <DChip label="Empfänger" value={m.stats.targeted ?? 0} />
                  {(m.channels || []).includes("push") && <DChip label="Push · Nutzer erreicht" value={m.stats.with_tokens ?? 0} />}
                  {(m.channels || []).includes("push") && <DChip label="Push · Geräte" value={m.stats.push_delivered ?? 0} />}
                  {(m.channels || []).includes("email") && <DChip label="E-Mails" value={m.stats.email_sent ?? 0} />}
                  {m.email_fallback && (m.stats.email_followup ?? 0) > 0 && <DChip label="E-Mail nachgefasst" value={m.stats.email_followup ?? 0} />}
                  {(m.channels || []).includes("inapp") && <DChip label="In-App" value={m.stats.inapp_created ?? 0} />}
                  {(m.channels || []).includes("feed") && <DChip label="Feed" value={m.stats.feed_posted ?? 0} />}
                  {(m.channels || []).includes("overlay") && <DChip label="Overlay an" value={m.stats.overlay_targeted ?? 0} />}
                </div>
              )}
              {m.ab_enabled && m.ab_auto_promote && (
                <p className="mt-1 text-[11px] text-primary" data-testid={`admin-message-promote-${m.id}`}>
                  {m.ab_promoted_at
                    ? `🏆 Auto-Nachversand: Gewinner ${m.ab_winner || "?"} an ${m.ab_promote_stats?.targeted ?? 0} Nutzer (${m.ab_promote_stats?.push_delivered ?? 0} Push) · ${dt(m.ab_promoted_at)}`
                    : `⏳ Auto-Nachversand geplant: Gewinner nach ${m.ab_promote_after_hours || 24} Std. an die schwächere Hälfte (Push)`}
                </p>
              )}
              {reports[m.id] && <ReportBox r={reports[m.id]} />}
            </div>
            <div className="flex shrink-0 gap-1.5">
              <Button size="sm" variant="outline" className="h-8 gap-1 rounded-full text-xs" onClick={() => onUseTemplate?.(m)} data-testid={`admin-message-template-${m.id}`}>
                <Copy className="h-3.5 w-3.5" /> Vorlage
              </Button>
              {m.status === "sent" && (
                <Button size="sm" variant="outline" className="h-8 gap-1 rounded-full text-xs" disabled={repBusy === m.id} onClick={() => loadReport(m)} data-testid={`admin-message-report-${m.id}`}>
                  <BarChart3 className="h-3.5 w-3.5" /> Wirkung
                </Button>
              )}
              {m.status === "scheduled" && (
                <Button size="sm" variant="outline" className="h-8 gap-1 rounded-full text-xs" disabled={busy === m.id} onClick={() => sendNow(m)} data-testid={`admin-message-sendnow-${m.id}`}>
                  <Send className="h-3.5 w-3.5" /> Jetzt
                </Button>
              )}
              {(m.status === "scheduled" || m.status === "draft") && (
                <Button size="icon" variant="outline" className="h-8 w-8 rounded-full text-rose-600" disabled={busy === m.id} onClick={() => del(m)} data-testid={`admin-message-delete-${m.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function pctRate(num, den) { return den > 0 ? Math.round((Number(num || 0) / den) * 1000) / 10 : 0; }

function DChip({ label, value }) {
  return (
    <span className="inline-flex items-baseline gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[11px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums text-foreground">{value}</span>
    </span>
  );
}

function ABMetric({ label, aRate, aCount, bRate, bCount }) {
  const max = Math.max(aRate, bRate, 0.0001);
  const rows = [["A", aRate, aCount, aRate >= bRate], ["B", bRate, bCount, bRate > aRate]];
  return (
    <div className="space-y-1">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      {rows.map(([key, rate, count, lead]) => (
        <div key={key} className="flex items-center gap-1.5" data-testid={`admin-report-bar-${label === "Öffnungsrate" ? "open" : "react"}-${key}`}>
          <span className={`w-3 shrink-0 text-[10px] font-bold ${lead ? "text-emerald-600" : "text-muted-foreground"}`}>{key}</span>
          <div className="relative h-2.5 flex-1 overflow-hidden rounded-full bg-secondary">
            <div className={`h-full rounded-full transition-all ${lead ? "bg-emerald-500" : "bg-muted-foreground/40"}`} style={{ width: `${(rate / max) * 100}%` }} />
          </div>
          <span className={`w-20 shrink-0 text-right text-[10px] tabular-nums ${lead ? "font-semibold text-emerald-600" : "text-muted-foreground"}`}>{rate}% · {count}</span>
        </div>
      ))}
    </div>
  );
}

function ReportBox({ r }) {
  const t = r.total || {};
  const a = r.variant_a || {};
  const b = r.variant_b || {};
  const ch = r.channels || [];
  return (
    <div className="mt-2 rounded-lg border border-border bg-secondary/40 p-2.5 text-[11px]" data-testid="admin-report-box">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Wirkung</p>
      <div className="flex flex-wrap gap-1.5" data-testid="admin-report-impact">
        <DChip label="In-App geöffnet" value={`${t.opened ?? 0} · ${t.open_rate ?? 0}%`} />
        <DChip label="Zurückgekehrt" value={`${t.reactivated ?? 0} · ${t.reactivation_rate ?? 0}%`} />
        {ch.includes("overlay") && <DChip label="Overlay gesehen" value={t.overlay_seen ?? 0} />}
        {ch.includes("overlay") && (
          <DChip label="Overlay geklickt" value={`${t.overlay_clicked ?? 0}${t.overlay_click_rate != null ? ` · ${t.overlay_click_rate}%` : ""}`} />
        )}
      </div>
      {r.ab_enabled && r.variant_a && r.variant_b && (
        <div className="mt-1.5" data-testid="admin-report-ab">
          <div className="mb-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
            <span className={r.winner === "A" ? "font-semibold text-emerald-600" : ""}>A · {a.title} {r.winner === "A" && "🏆"}</span>
            <span className={r.winner === "B" ? "font-semibold text-emerald-600" : ""}>B · {b.title} {r.winner === "B" && "🏆"}</span>
          </div>
          <div className="space-y-2" data-testid="admin-report-ab-bars">
            <ABMetric label="Öffnungsrate" aRate={pctRate(a.opened, a.targeted)} aCount={a.opened ?? 0} bRate={pctRate(b.opened, b.targeted)} bCount={b.opened ?? 0} />
            <ABMetric label="Reaktivierungsrate" aRate={pctRate(a.reactivated, a.targeted)} aCount={a.reactivated ?? 0} bRate={pctRate(b.reactivated, b.targeted)} bCount={b.reactivated ?? 0} />
          </div>
        </div>
      )}
      {r.ab_auto_promote && (
        <p className="mt-1.5 text-primary" data-testid="admin-report-promote">
          {r.ab_promoted_at
            ? `Auto-Nachversand erfolgt (Gewinner ${r.winner || r.ab_promote_stats?.winner || "?"}) · ${r.ab_promote_stats?.push_delivered ?? 0} Push an ${r.ab_promote_stats?.targeted ?? 0} Nutzer`
            : `Auto-Nachversand nach ${r.ab_promote_after_hours || 24} Std. geplant`}
        </p>
      )}
    </div>
  );
}

/* ───────────────────────── Campaigns ───────────────────────── */

function CampaignsTab({ campaigns, onChanged }) {
  const [name, setName] = useState("");
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (name.trim().length < 2) { toast.error("Bitte einen Namen eingeben."); return; }
    setBusy(true);
    try {
      await api.post("/admin/campaigns", { name: name.trim(), goal: goal.trim() || undefined });
      toast.success("Kampagne erstellt.");
      setName(""); setGoal(""); onChanged?.();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Section title="Neue Kampagne" testId="admin-campaigns-create">
        <div className="space-y-3">
          <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Reaktivierung November" data-testid="admin-campaign-new-name" /></Field>
          <Field label="Ziel (optional)"><Textarea value={goal} onChange={(e) => setGoal(e.target.value)} className="min-h-[60px]" placeholder="Was soll diese Kampagne erreichen?" data-testid="admin-campaign-new-goal" /></Field>
          <Button className="gap-1.5 rounded-full" onClick={create} disabled={busy} data-testid="admin-campaign-new-submit"><Plus className="h-4 w-4" /> Kampagne anlegen</Button>
        </div>
      </Section>

      {campaigns.length === 0 ? <Empty text="Noch keine Kampagnen." /> : (
        <div className="space-y-2">
          {campaigns.map((c) => (
            <div key={c.id} data-testid={`admin-campaign-row-${c.id}`} className="rounded-xl border border-border bg-card p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-semibold text-foreground">{c.name}</p>
                <Pill tone="info">{c.message_count ?? 0} Nachrichten</Pill>
              </div>
              {c.goal && <p className="mt-0.5 text-xs text-muted-foreground">{c.goal}</p>}
              <p className="mt-1 text-[11px] text-muted-foreground">erstellt: {dt(c.created_at)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Settings ───────────────────────── */

function SettingsTab() {
  const [recipient, setRecipient] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/campaigns/settings");
        setRecipient(data?.ab_promote_email_recipient || "");
      } catch (err) { toast.error(apiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/campaigns/settings", { ab_promote_email_recipient: recipient.trim() });
      setRecipient(data?.ab_promote_email_recipient || "");
      toast.success("Einstellungen gespeichert.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-4" data-testid="admin-campaigns-settings">
      <Section title="System-Benachrichtigungen" testId="admin-campaigns-settings-section">
        <div className="space-y-3">
          <div className="flex items-start gap-2 rounded-xl border border-border bg-secondary/40 p-3 text-sm text-muted-foreground">
            <Settings className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>
              E-Mail-Empfänger für automatische A/B-Gewinner-Nachversendungen. Mehrere Adressen durch Komma trennen.
              Leer lassen = alle Admins mit zustellbarer Adresse. Die In-App-Benachrichtigung erreicht weiterhin alle Admins.
            </span>
          </div>
          <Field label="E-Mail-Empfänger (Override)">
            <Input
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="z. B. ops@my-within.com, gruender@my-within.com"
              disabled={loading}
              data-testid="admin-campaigns-settings-recipient"
            />
          </Field>
          <Button className="gap-1.5 rounded-full" onClick={save} disabled={saving || loading} data-testid="admin-campaigns-settings-save">
            <Save className="h-4 w-4" /> {saving ? "Speichert …" : "Speichern"}
          </Button>
        </div>
      </Section>
    </div>
  );
}

